import textIntegrationContent from '@/data/textIntegrationContent.json'
import { isPublic, PRODUCT } from '@/utils/environment'
import { getCrossListedProducts } from './compatibility'

// Platforms, channels and automations documented by Text.
// Sources for each connection are recorded in textIntegrationContent.json.
// Match exact catalogue slugs; Salesforce CRM is not Salesforce Commerce Cloud.
export const textIntegrations = [
  {
    name: 'Shopify',
    slug: 'shopify',
    group: 'ecommerce',
    aliases: ['shopify-live-chat', 'shopify-20'],
  },
  { name: 'WooCommerce', slug: 'woocommerce', group: 'ecommerce', aliases: [] },
  { name: 'WordPress', slug: 'wordpress', group: 'website', aliases: [] },
  {
    name: 'Webflow',
    slug: 'webflow',
    group: 'website',
    aliases: ['webflow-1'],
  },
  { name: 'BigCommerce', slug: 'bigcommerce', group: 'ecommerce', aliases: [] },
  { name: 'Magento', slug: 'magento', group: 'ecommerce', aliases: [] },
  {
    name: 'Adobe Commerce',
    slug: 'adobe-commerce',
    group: 'ecommerce',
    aliases: ['magento-2'],
  },
  {
    name: 'Salesforce Commerce Cloud',
    slug: 'salesforce-commerce-cloud',
    group: 'ecommerce',
    aliases: [],
  },
  {
    name: 'SAP Commerce Cloud',
    slug: 'sap-commerce-cloud',
    group: 'ecommerce',
    aliases: [],
  },
  {
    name: 'commercetools',
    slug: 'commercetools',
    group: 'ecommerce',
    aliases: [],
  },
  { name: 'Medusa', slug: 'medusa', group: 'ecommerce', aliases: [] },
  { name: 'Swell', slug: 'swell', group: 'ecommerce', aliases: [] },
  {
    name: 'WhatsApp',
    slug: 'whatsapp',
    group: 'messaging',
    aliases: ['whatsapp-business-dev', 'whatsapp-business'],
  },
  {
    name: 'Messenger',
    slug: 'facebook-messenger',
    group: 'messaging',
    aliases: ['messenger'],
  },
  { name: 'Mailchimp', slug: 'mailchimp', group: 'marketing', aliases: [] },
  { name: 'Klaviyo', slug: 'klaviyo', group: 'marketing', aliases: [] },
  { name: 'Instagram', slug: 'instagram', group: 'messaging', aliases: [] },
]

const integrationBySlug = new Map(
  textIntegrations.flatMap((integration) =>
    [integration.slug, ...integration.aliases].map((slug) => [
      slug,
      integration,
    ])
  )
)

export const getTextIntegration = (app) => integrationBySlug.get(app?.slug)

export const getTextIntegrationSlugs = (slug) => {
  const integration = integrationBySlug.get(slug)
  return integration ? [integration.slug, ...integration.aliases] : [slug]
}

export const TEXT_SIGN_UP_LABEL = 'Start free trial'

export const TEXT_SIGN_UP_URL =
  'https://accounts.livechat.com/signup?client_id=fc7bf1555adb6d3d2a441f10fd5b6eb5&redirect_uri=https%3A%2F%2Fwww.text.com%2Fapp&response_type=token&source_type=website&source_id=marketplace'

export const getTextSignUpUrl = (slug) =>
  `${TEXT_SIGN_UP_URL}&source_url=${encodeURIComponent(
    `${process.env.GATSBY_PRODUCT_WEBSITE_URL}/marketplace/apps/${slug}/`
  )}`

// Public pages present Text integrations as Text apps, whatever the catalog says.
export const getPublicHomeProduct = (app) =>
  isPublic && getTextIntegration(app)
    ? PRODUCT.Text
    : app?.product || PRODUCT.LiveChat

export const worksWithText = (app) =>
  getCrossListedProducts(app).includes(PRODUCT.Text) ||
  !!getTextIntegration(app)

export const isContactExpertApp = (app) =>
  app?.customProps?.isContactExpert === 'true'

export const prioritizeTextIntegrations = (apps = []) => [
  ...apps.filter((app) => getTextIntegration(app)),
  ...apps.filter((app) => !getTextIntegration(app)),
]

export const getRecommendedTextIntegrations = (apps = []) =>
  textIntegrations.flatMap((integration) => {
    const app = apps.find(
      (app) =>
        getTextIntegration(app) === integration &&
        !app.hidden &&
        !app.underMaintenance &&
        app.customProps?.isService !== 'true'
    )
    return app ? [app] : []
  })

export const getTextIntegrationsInGroups = (apps = [], groups = []) =>
  getRecommendedTextIntegrations(apps)
    .filter((app) => groups.includes(getTextIntegration(app).group))
    .map((app) => ({
      ...app,
      name: getTextIntegration(app).name,
      shortDescription: getTextIntegrationDescription(app),
    }))

export const getTextIntegrationDescription = (app) => {
  const integration = getTextIntegration(app)
  return integration
    ? textIntegrationContent[integration.slug]?.shortDescription
    : undefined
}
